import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { Btn, Tag, Card, Avatar, QGlyph, Toggle, Sidebar, MobileNav, ServerBadge, Icons, LoadingScreen, useToast } from './ui';
import { TranslateText } from './TranslateText';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserInfo {
  user_id: string;
  display_name: string;
  profile_img: string;
  server: string;
  accepting: boolean;
  app: string;
  notify: boolean;
  reject_sensitive_word: boolean;
  loginonly: boolean;
  box_name: string | null;
  box_type: string | null;
}

export interface Question {
  id: number;
  server: string;
  user_id: string;
  question: string;
  answer: string;
  nsfw: boolean;
  created_at: string;
  answerd_at: string | null;
  not_anonymous: boolean;
  questioner_user_id: string;
  questioner_server: string;
}

type ViewLevel = 'not_answerd' | 'answerd' | 'all';
type Visibility = 'public' | 'unlisted' | 'private';

const UTCtoJST = (d: string) =>
  moment.utc(d).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');

// ─── Answer Modal ────────────────────────────────────────────────────────────

const AnswerModal: React.FC<{
  question: Question;
  userInfo: UserInfo;
  onClose: () => void;
  onAnswered: () => void;
}> = ({ question, userInfo, onClose, onAnswered }) => {
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [nsfw, setNsfw] = useState(false);
  const [reply, setReply] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/answer/${question.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: text, nsfw, visibility, reply }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || t('mypage.answerFailed'));
        return;
      }
      onAnswered();
      onClose();
    } catch {
      setError(t('mypage.answerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white dark:bg-ink-900 rounded-3xl shadow-pop overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-150 dark:border-ink-800">
          <div className="flex items-center gap-2">
            <QGlyph tone="blue" size={28} />
            <span className="font-bold">{t('mypage.answerModalTitle')}</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 dark:hover:bg-ink-850 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-4 bg-ink-50/60 dark:bg-ink-950/30 border-b border-ink-150 dark:border-ink-800">
          <div className="flex items-center gap-2 mb-2 text-[12px] text-ink-500">
            <Avatar anonymous={!question.not_anonymous} seed={question.questioner_user_id} size={20} />
            <span>
              {question.not_anonymous
                ? `@${question.questioner_user_id}@${question.questioner_server}`
                : t('common.anonymous')}
            </span>
            <span className="ml-auto">{UTCtoJST(question.created_at)}</span>
          </div>
          <p className="text-[16px] font-semibold leading-relaxed">{question.question}</p>
        </div>

        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-3 text-[12px] text-ink-500 dark:text-ink-400">
            <Avatar src={userInfo.profile_img} seed={userInfo.display_name} size={24} />
            <span className="font-semibold">{userInfo.display_name}</span>
            <span className="font-mono">@{userInfo.user_id}@{userInfo.server}</span>
            <div className="ml-auto flex items-center gap-1 text-[11px] text-ink-500 dark:text-ink-400">
              <ServerBadge host={userInfo.server} />
              <span>{t('mypage.promoteTo')}</span>
            </div>
          </div>
          <textarea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('mypage.placeholderAnswer')}
            className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-4 text-[15px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-blue-300 transition"
          />
          <div className="flex justify-end text-[12px] text-ink-400 mt-1">
            <span className={text.length > 500 ? 'text-rose-500' : ''}>{text.length}</span>
            <span> / 500</span>
          </div>
        </div>

        <div className="px-6 pb-4 space-y-3 mt-2">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{t('mypage.visibilityLabel')}</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800">
              {([
                { v: 'public' as Visibility, l: t('common.public'), i: Icons.Globe },
                { v: 'unlisted' as Visibility, l: t('common.unlisted'), i: Icons.Compass },
                { v: 'private' as Visibility, l: t('common.follower'), i: Icons.Lock },
              ] as { v: Visibility; l: string; i: React.FC<any> }[]).map((o) => {
                const on = visibility === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => setVisibility(o.v)}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] font-medium transition ${
                      on
                        ? 'bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100'
                        : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-100'
                    }`}
                  >
                    <o.i width={13} height={13} /> {o.l}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-6 text-[13px]">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} className="rounded" />
              {t('common.nsfw')}
            </label>
            <label className={`flex items-center gap-2 ${question.not_anonymous ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
              <input
                type="checkbox"
                checked={reply}
                onChange={(e) => setReply(e.target.checked)}
                disabled={!question.not_anonymous}
                className="rounded disabled:cursor-not-allowed"
              />
              {t('mypage.replyLabel')}
              {!question.not_anonymous && (
                <span className="text-[11px] text-ink-400">{t('mypage.replyDisabled')}</span>
              )}
            </label>
          </div>
          {error && <p className="text-[12px] text-rose-500">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-ink-150 dark:border-ink-800 flex gap-3 justify-end">
          <Btn variant="ghost" size="md" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!text.trim() || loading}
          >
            <Icons.Sparkle width={14} height={14} />
            {loading ? t('common.posting') : t('mypage.postAnswer')}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ─── Report Modal ─────────────────────────────────────────────────────────────

const ReportModal: React.FC<{
  questionId: number;
  onClose: () => void;
}> = ({ questionId, onClose }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      await fetch(`/api/v1/question/report/${questionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_reason: reason }),
      });
      setDone(true);
      setTimeout(onClose, 1500);
    } catch {
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <Card className="w-full max-w-md !p-6">
        <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
          <Icons.Flag width={18} height={18} className="text-rose-500" /> {t('mypage.reportModalTitle')}
        </h3>
        <p className="text-[13px] text-ink-500 dark:text-ink-400 mb-4">
          {t('mypage.reportDesc')}
        </p>
        {done ? (
          <p className="text-brand-green-600 font-semibold text-center py-4">{t('common.reported')}</p>
        ) : (
          <>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('mypage.reportPlaceholder')}
              className="w-full bg-ink-100 dark:bg-ink-850 rounded-xl p-3 text-[14px] resize-none outline-none placeholder:text-ink-400 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" size="md" onClick={onClose}>{t('common.cancel')}</Btn>
              <Btn
                variant="danger"
                size="md"
                onClick={handleSubmit}
                disabled={!reason.trim() || loading}
              >
                <Icons.Flag width={14} height={14} /> {t('common.reportVerb')}
              </Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// ─── QuestionCard ─────────────────────────────────────────────────────────────

const QuestionCard: React.FC<{
  q: Question;
  isMine: boolean;
  onAnswer: (q: Question) => void;
  onDelete: (id: number) => void;
  onReport: (id: number) => void;
}> = ({ q, isMine, onAnswer, onDelete, onReport }) => {
  const answered = !!q.answer;
  const { t } = useTranslation();

  return (
    <Card className="!p-5">
      <div className="flex items-start gap-3 mb-3">
        <Avatar anonymous={!q.not_anonymous} seed={q.questioner_user_id || 'A'} size={36} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[12px] text-ink-500 dark:text-ink-400">
              {q.not_anonymous
                ? `@${q.questioner_user_id}@${q.questioner_server}`
                : t('common.anonymous')}
            </span>
            {q.nsfw && <Tag tone="outline" className="!text-rose-500 !border-rose-200">NSFW</Tag>}
            <span className="text-[11px] text-ink-400 ml-auto">{UTCtoJST(q.created_at)}</span>
          </div>
          <p className="text-[15px] font-semibold leading-snug"><TranslateText text={q.question} inline /></p>
        </div>
      </div>

      {answered && (
        <div className="border-t border-dashed border-ink-150 dark:border-ink-800 pt-3 flex items-start gap-3 mt-3">
          <QGlyph tone="blue" size={24} />
          <div className="flex-1">
            <p className="text-[14px] leading-relaxed text-ink-700 dark:text-ink-200"><TranslateText text={q.answer} inline /></p>
            {q.answerd_at && (
              <p className="text-[11px] text-ink-400 mt-1">{UTCtoJST(q.answerd_at)}</p>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-ink-150 dark:border-ink-800">
        {isMine && !answered && (
          <Btn variant="primary" size="sm" onClick={() => onAnswer(q)}>
            <Icons.Reply width={13} height={13} /> {t('common.answerVerb')}
          </Btn>
        )}
        {isMine && (
          <Btn variant="danger" size="sm" onClick={() => onDelete(q.id)}>
            <Icons.Trash width={13} height={13} /> {t('common.delete')}
          </Btn>
        )}
        <button
          onClick={() => onReport(q.id)}
          className="ml-auto h-8 w-8 rounded-full flex items-center justify-center text-ink-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition"
          title={t('common.report')}
        >
          <Icons.Flag width={14} height={14} />
        </button>
      </div>
    </Card>
  );
};

// ─── QuestionInbox ────────────────────────────────────────────────────────────

const QuestionInbox: React.FC<{
  viewLevel: ViewLevel;
  onSelectAnswer: (q: Question) => void;
  onSelectReport: (id: number) => void;
  refreshKey: number;
}> = ({ viewLevel, onSelectAnswer, onSelectReport, refreshKey }) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/questions/${page}?view_level=${viewLevel}`);
      if (res.ok) setQuestions(await res.json());
    } catch {}
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, viewLevel, refreshKey]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  useEffect(() => { setPage(0); }, [viewLevel]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('mypage.deleteConfirm'))) return;
    try {
      await fetch(`/api/v1/delete/${id}`, { method: 'DELETE' });
      setQuestions((qs) => qs.filter((q) => q.id !== id));
    } catch {}
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-ink-400 flex flex-col items-center gap-3">
        <div className="w-7 h-7 rounded-full border-2 border-ink-200 dark:border-ink-700 border-t-brand-blue-400 animate-spin" />
        <span className="text-[13px]">{t('common.loading')}</span>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="text-center py-12 text-ink-400">
        {viewLevel === 'not_answerd' ? t('mypage.emptyNotAnswered') : t('mypage.emptyQuestions')}
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4">
        {questions.map((q) => (
          <QuestionCard
            key={q.id}
            q={q}
            isMine
            onAnswer={onSelectAnswer}
            onDelete={handleDelete}
            onReport={onSelectReport}
          />
        ))}
      </div>
      {questions.length === 20 && (
        <div className="flex justify-center gap-2 mt-6">
          {page > 0 && (
            <Btn variant="outline" size="sm" onClick={() => setPage((p) => p - 1)}>
              ← {t('common.prev')}
            </Btn>
          )}
          <Btn variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
            {t('common.next')} →
          </Btn>
        </div>
      )}
    </div>
  );
};

// ─── Settings Panel ───────────────────────────────────────────────────────────

type ThemePref = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { v: ThemePref; l: string; Icon: React.FC<React.SVGAttributes<SVGElement>> }[] = [
  { v: 'light',  l: 'Light',   Icon: Icons.Sun },
  { v: 'dark',   l: 'Dark',   Icon: Icons.Moon },
  { v: 'system', l: 'System', Icon: Icons.Computer },
];

function getStoredTheme(): ThemePref {
  const stored = localStorage.getItem('theme');
  if (stored === 'dark') return 'dark';
  if (stored === 'light') return 'light';
  return 'system';
}

function applyThemePref(t: ThemePref) {
  if (t === 'dark') {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else if (t === 'light') {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  } else {
    localStorage.removeItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.toggle('dark', prefersDark);
  }
}

const PRESET_BOX_TYPES = ['質問', 'お題', '相談', 'メッセージ'];

const SettingsPanel: React.FC<{ userInfo: UserInfo }> = ({ userInfo }) => {
  const initialBoxType = userInfo.box_type ?? '質問';
  const [boxName, setBoxName] = useState(userInfo.box_name ?? '');
  const [boxType, setBoxType] = useState(initialBoxType);
  const [customDraft, setCustomDraft] = useState(
    PRESET_BOX_TYPES.includes(initialBoxType) ? '' : initialBoxType
  );
  const [accepting, setAccepting] = useState(userInfo.accepting);
  const [notify, setNotify] = useState(userInfo.notify);
  const [rejectSensitive, setRejectSensitive] = useState(userInfo.reject_sensitive_word);
  const [loginOnly, setLoginOnly] = useState(userInfo.loginonly);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<ThemePref>(getStoredTheme);
  const { show: flash, ToastEl } = useToast();
  const { t } = useTranslation();

  const handleTheme = (t: ThemePref) => {
    setTheme(t);
    applyThemePref(t);
  };

  const toggle = async (endpoint: string, value: boolean, setter: (v: boolean) => void) => {
    try {
      await fetch(`/api/v1/question/${endpoint}/${value}`, { method: 'POST' });
      setter(value);
      flash(t('mypage.saved'));
    } catch {
      flash(t('mypage.saveFailed'));
    }
  };

  const saveBoxName = async () => {
    setSaving(true);
    try {
      await fetch('/api/v1/question/boxname', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ box_name: boxName }),
      });
      flash(t('mypage.boxNameSaved'));
    } catch {
      flash(t('mypage.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const saveBoxType = async (value: string) => {
    setBoxType(value);
    try {
      await fetch('/api/v1/question/boxtype', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ box_type: value }),
      });
      flash(t('mypage.boxTypeSaved'));
    } catch {
      flash(t('mypage.saveFailed'));
    }
  };

  const saveCustomBoxType = async () => {
    const v = customDraft.trim();
    if (!v) return;
    await saveBoxType(v);
  };

  const handleLogout = async () => {
    await fetch('/api/v1/logout', { method: 'POST' });
    window.location.href = '/';
  };

  const copyUrl = () => {
    const url = `${window.location.origin}/questionbox/@${userInfo.user_id}@${userInfo.server}`;
    navigator.clipboard.writeText(url).then(() => flash(t('common.copied')));
  };

  return (
    <div className="space-y-6">
      {ToastEl}

      <Card className="!p-6">
        <div className="flex items-center gap-4 mb-4">
          <Avatar src={userInfo.profile_img} seed={userInfo.display_name} size={56} />
          <div>
            <div className="text-lg font-bold">{userInfo.display_name}</div>
            <div className="text-[13px] text-ink-500 dark:text-ink-400 font-mono">
              @{userInfo.user_id}@{userInfo.server}
            </div>
            <ServerBadge host={userInfo.server} className="mt-1" />
          </div>
        </div>
        <Btn variant="danger" size="sm" onClick={handleLogout}>
          <Icons.Logout width={13} height={13} /> {t('common.logout')}
        </Btn>
      </Card>

      <Card className="!p-6 space-y-4">
        <h3 className="text-[13px] font-bold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('mypage.basic')}</h3>
        <div>
          <label className="text-[13px] font-semibold mb-2 block">{t('mypage.boxNameLabel')}</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={boxName}
              onChange={(e) => setBoxName(e.target.value)}
              maxLength={30}
              placeholder={t('mypage.boxNamePlaceholder')}
              className="flex-1 h-11 rounded-xl bg-ink-100 dark:bg-ink-850 px-4 text-[14px] outline-none focus:ring-2 focus:ring-brand-blue-300 transition"
            />
            <Btn variant="primary" size="md" onClick={saveBoxName} disabled={saving}>
              <Icons.Check width={14} height={14} /> {saving ? t('common.saving') : t('common.save')}
            </Btn>
          </div>
          <div className="text-right text-[11px] text-ink-400 mt-1">{boxName.length} / 30</div>
        </div>
        <div>
          <label className="text-[13px] font-semibold mb-2 block">{t('mypage.boxTypeLabel')}</label>
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800 w-fit">
            {PRESET_BOX_TYPES.map((bt) => (
              <button
                key={bt}
                onClick={() => { setCustomDraft(''); saveBoxType(bt); }}
                className={`px-4 h-9 rounded-lg text-[13px] font-semibold transition ${
                  boxType === bt && PRESET_BOX_TYPES.includes(boxType)
                    ? 'bg-white dark:bg-ink-900 text-brand-blue-600 dark:text-brand-blue-300 shadow-sm'
                    : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100'
                }`}
              >
                {bt}
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input
              type="text"
              value={customDraft}
              onChange={(e) => setCustomDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveCustomBoxType()}
              placeholder={t('mypage.boxTypeCustomPlaceholder')}
              maxLength={20}
              className={`h-9 w-52 rounded-xl bg-ink-100 dark:bg-ink-850 px-3 text-[13px] outline-none focus:ring-2 focus:ring-brand-blue-300 transition ${
                !PRESET_BOX_TYPES.includes(boxType) && boxType === customDraft
                  ? 'ring-2 ring-brand-blue-300'
                  : ''
              }`}
            />
            {customDraft.trim() && (
              <Btn variant="soft" size="sm" onClick={saveCustomBoxType}>
                <Icons.Check width={12} height={12} /> {t('common.save')}
              </Btn>
            )}
          </div>
          <p className="text-[11px] text-ink-400 mt-2">
            {t('mypage.boxTypeHint', { boxType })}
          </p>
        </div>
        <div>
          <label className="text-[13px] font-semibold mb-2 block">{t('mypage.urlLabel')}</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-11 rounded-xl bg-ink-100 dark:bg-ink-850 px-4 flex items-center font-mono text-[13px] text-ink-600 dark:text-ink-300 truncate">
              {window.location.host}/questionbox/@{userInfo.user_id}@{userInfo.server}
            </div>
            <Btn variant="soft" size="md" onClick={copyUrl}>
              <Icons.Link width={14} height={14} /> {t('mypage.copy')}
            </Btn>
          </div>
        </div>
      </Card>

      <Card className="!p-6 space-y-3">
        <h3 className="text-[13px] font-bold uppercase tracking-wider text-ink-500 dark:text-ink-400 mb-1">{t('mypage.receiving')}</h3>
        <Toggle
          value={accepting}
          onChange={(v) => toggle('public', v, setAccepting)}
          title={t('mypage.acceptingTitle')}
          description={t('mypage.acceptingDesc')}
        />
        <Toggle
          value={notify}
          onChange={(v) => toggle('notify', v, setNotify)}
          title={t('mypage.notifyTitle')}
          description={t('mypage.notifyDesc')}
          accent="blue"
        />
        <Toggle
          value={rejectSensitive}
          onChange={(v) => toggle('reject', v, setRejectSensitive)}
          title={t('mypage.rejectTitle')}
          description={t('mypage.rejectDesc')}
        />
        <Toggle
          value={loginOnly}
          onChange={(v) => toggle('loginonly', v, setLoginOnly)}
          title={t('mypage.loginOnlyTitle')}
          description={t('mypage.loginOnlyDesc')}
          accent="blue"
        />
        <div className="flex flex-wrap gap-2 pt-2">
          <Tag tone={accepting ? 'green' : 'outline'}>{accepting ? t('mypage.statusAccepting') : t('mypage.statusStopped')}</Tag>
          <Tag tone={notify ? 'blue' : 'outline'}>{notify ? t('mypage.statusDmOn') : t('mypage.statusDmOff')}</Tag>
          <Tag tone={rejectSensitive ? 'green' : 'outline'}>{rejectSensitive ? t('mypage.statusNgOn') : t('mypage.statusNgOff')}</Tag>
          <Tag tone={loginOnly ? 'blue' : 'outline'}>{loginOnly ? t('mypage.statusLoginOnly') : t('mypage.statusAnyone')}</Tag>
        </div>
      </Card>

      <Card className="!p-6 space-y-4">
        <h3 className="text-[13px] font-bold uppercase tracking-wider text-ink-500 dark:text-ink-400">{t('mypage.appearance')}</h3>
        <div>
          <label className="text-[13px] font-semibold mb-2 block">{t('mypage.themeLabel')}</label>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800 w-fit">
            {THEME_OPTIONS.map((o) => (
              <button
                key={o.v}
                onClick={() => handleTheme(o.v)}
                className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-lg text-[13px] font-semibold transition ${
                  theme === o.v
                    ? 'bg-white dark:bg-ink-900 text-brand-blue-600 dark:text-brand-blue-300 shadow-sm'
                    : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100'
                }`}
              >
                <o.Icon width={14} height={14} /> {o.v === 'light' ? t('mypage.themeLight') : o.v === 'dark' ? t('mypage.themeDark') : t('mypage.themeSystem')}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-ink-400 mt-2">
            {theme === 'system' ? t('mypage.themeSystemDesc') : theme === 'dark' ? t('mypage.themeDarkDesc') : t('mypage.themeLightDesc')}
          </p>
        </div>
      </Card>
    </div>
  );
};

// ─── RightSidebar ─────────────────────────────────────────────────────────────

export interface ActivityDay {
  date: string;
  count: number;
}

export interface ActivityData {
  days: ActivityDay[];
  total: number;
}

export interface MiniCommunityQ {
  id: number;
  display_name: string;
  avatar: string;
  question: string;
  answer_count: number;
  is_sensitive?: boolean;
}

export const LEVEL_CLASSES = [
  'bg-ink-150 dark:bg-ink-800',
  'bg-brand-blue-100 dark:bg-brand-blue-900/40',
  'bg-brand-blue-200 dark:bg-brand-blue-800',
  'bg-brand-blue-400',
  'bg-brand-blue-500',
];

export function countToLevel(n: number): number {
  if (n === 0) return 0;
  if (n === 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  return 4;
}

export const RightSidebar: React.FC<{
  userInfo: UserInfo;
  onAnswer?: (q: Question) => void;
  refreshKey?: number;
}> = ({ userInfo, onAnswer, refreshKey = 0 }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const AppIcon = userInfo.app === 'misskey' ? Icons.Misskey : Icons.Mastodon;
  const appLabel = userInfo.app === 'misskey' ? 'Misskey' : 'Mastodon';
  const [unansweredQs, setUnansweredQs] = useState<Question[]>([]);
  const [unansweredTotal, setUnansweredTotal] = useState(0);
  const [communityQs, setCommunityQs] = useState<MiniCommunityQ[]>([]);

  useEffect(() => {
    fetch('/api/v1/questions/0?view_level=not_answerd')
      .then((r) => r.ok ? r.json() : [])
      .then((data: Question[]) => setUnansweredQs(data.slice(0, 4)))
      .catch(() => {});
    fetch('/api/v1/count/questions')
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((data: { count: number }) => setUnansweredTotal(data.count))
      .catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    fetch('/api/v1/community/questions?page=0')
      .then((r) => r.ok ? r.json() : [])
      .then((data: MiniCommunityQ[]) => setCommunityQs(data.filter((q) => !q.is_sensitive).slice(0, 3)))
      .catch(() => {});
  }, []);


  return (
    <div className="p-5 space-y-5">
      {/* 未回答キュー */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-ink-400 dark:text-ink-500">
            {t('mypage.inbox')}
          </h3>
          {unansweredTotal > 0 && (
            <span className="bg-brand-green-500 text-white text-[10px] font-bold rounded-full px-2 h-4 inline-flex items-center">
              {t('common.countItems', { count: unansweredTotal })}
            </span>
          )}
        </div>
        <div className="space-y-1.5">
          {unansweredQs.length === 0 ? (
            <p className="text-[12px] text-ink-400 dark:text-ink-500 text-center py-3">
              {t('mypage.inboxEmpty')}
            </p>
          ) : (
            unansweredQs.map((q) => (
              <button
                key={q.id}
                onClick={() => onAnswer ? onAnswer(q) : navigate('/mypage')}
                className="w-full text-left p-3 rounded-xl bg-ink-100/60 dark:bg-ink-850/60 hover:bg-brand-blue-50 dark:hover:bg-brand-blue-900/20 transition group"
              >
                <div className="flex items-center gap-1 mb-1 text-[11px] text-ink-400 dark:text-ink-500">
                  <Avatar anonymous={!q.not_anonymous} seed={q.questioner_user_id || 'A'} size={14} />
                  <span className="truncate">
                    {q.not_anonymous ? `@${q.questioner_user_id}@${q.questioner_server}` : t('common.anonymous')}
                  </span>
                </div>
                <p className="text-[13px] font-semibold line-clamp-2 group-hover:text-brand-blue-600 dark:group-hover:text-brand-blue-300 transition-colors">
                  <TranslateText text={q.question} inline />
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* みんなに質問 */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-ink-400 dark:text-ink-500">
            {t('nav.community')}
          </h3>
          <button
            onClick={() => navigate('/community')}
            className="text-[10px] text-brand-blue-500 hover:underline"
          >
            {t('common.seeAll')} →
          </button>
        </div>
        <div className="space-y-1.5">
          {communityQs.length === 0 ? (
            <p className="text-[12px] text-ink-400 dark:text-ink-500 text-center py-3">
              {t('mypage.emptyQuestions')}
            </p>
          ) : (
            communityQs.map((q) => (
              <button
                key={q.id}
                onClick={() => navigate('/community')}
                className="w-full text-left p-3 rounded-xl bg-ink-100/60 dark:bg-ink-850/60 hover:bg-brand-green-50 dark:hover:bg-brand-green-900/20 transition group"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Avatar src={q.avatar} seed={q.display_name} size={14} />
                  <span className="text-[11px] text-ink-400 truncate flex-1">{q.display_name}</span>
                  <span className="text-[10px] text-ink-400 flex items-center gap-0.5 shrink-0">
                    <Icons.Reply width={10} height={10} /> {q.answer_count}
                  </span>
                </div>
                <p className="text-[13px] font-semibold line-clamp-2 group-hover:text-brand-green-600 dark:group-hover:text-brand-green-300 transition-colors">
                  <TranslateText text={q.question} inline />
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* 連携サーバー */}
      <div>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-ink-400 dark:text-ink-500 mb-3">
          {t('mypage.serverLabel')}
        </h3>
        <Card className="!p-4">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-ink-100 dark:bg-ink-850 flex items-center justify-center text-ink-600 dark:text-ink-300">
              <AppIcon width={20} height={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold">{appLabel}</div>
              <div className="text-[11px] text-ink-500 dark:text-ink-400 font-mono truncate">
                {userInfo.server}
              </div>
            </div>
            <span className="text-[11px] font-semibold text-brand-green-600 dark:text-brand-green-400 shrink-0">
              {t('mypage.serverConnected')}
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-ink-150 dark:border-ink-800">
            <ServerBadge host={userInfo.server} size="md" />
          </div>
        </Card>
      </div>

      {/* 姉妹サービス */}
      <div>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-ink-400 dark:text-ink-500 mb-3">
          {t('top.sisterTag')}
        </h3>
        <div className="space-y-2">
          <a
            href="https://purofu.y-zu.org"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 hover:bg-brand-blue-50 dark:hover:bg-brand-blue-900/20 transition group"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-brand-blue-50 dark:bg-brand-blue-900/40 shrink-0">
              <img src="/purofu_logo.png" alt="Fediverseプロフ帳" className="w-full h-full object-contain p-1" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold">Fediverseプロフ帳</div>
              <div className="text-[11px] text-ink-500 dark:text-ink-400 line-clamp-2">{t('top.sisterPurofu')}</div>
            </div>
            <Icons.ExternalLink width={12} height={12} className="text-ink-400 group-hover:text-brand-blue-500 shrink-0" />
          </a>
          <a
            href="https://tootori.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-2xl bg-ink-100/60 dark:bg-ink-850/60 hover:bg-[#c43a61]/10 dark:hover:bg-[#c43a61]/20 transition group"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-[#c43a61]/10 dark:bg-[#c43a61]/20 shrink-0">
              <img src="/tootori_logo.svg" alt="tootori" className="w-full h-full object-contain p-1" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold">tootori</div>
              <div className="text-[11px] text-ink-500 dark:text-ink-400 line-clamp-2">{t('top.sisterTootori')}</div>
            </div>
            <Icons.ExternalLink width={12} height={12} className="text-ink-400 group-hover:text-[#c43a61] shrink-0" />
          </a>
        </div>
      </div>
    </div>
  );
};

// ─── FediversePromoteModal ───────────────────────────────────────────────────

const FediversePromoteModal: React.FC<{
  userInfo: UserInfo;
  boxUrl: string;
  onClose: () => void;
  onPosted: () => void;
}> = ({ userInfo, boxUrl, onClose, onPosted }) => {
  const { t } = useTranslation();
  const boxType = userInfo.box_type ?? '質問';
  const boxName = userInfo.box_name || `${userInfo.display_name}の${boxType}箱`;
  const fullUrl = `${window.location.origin}${boxUrl}`;
  const defaultText = t('mypage.promoteDefaultText', { boxName, boxType, url: fullUrl });

  const [text, setText] = useState(defaultText);
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const charMax = 500;

  const handlePost = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/v1/promote/box', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, visibility }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || t('community.postFailed'));
        return;
      }
      onPosted();
      onClose();
    } catch {
      setError(t('community.postFailed'));
    } finally {
      setLoading(false);
    }
  };

  const visibilityOptions: { v: Visibility; l: string; i: React.FC<React.SVGAttributes<SVGElement>> }[] = [
    { v: 'public',   l: t('common.public'),      i: Icons.Globe },
    { v: 'unlisted', l: t('common.unlisted'),    i: Icons.Compass },
    { v: 'private',  l: t('common.follower'), i: Icons.Lock },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-ink-900 rounded-3xl shadow-pop overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-150 dark:border-ink-800">
          <div className="flex items-center gap-2">
            <Icons.Globe width={20} height={20} className="text-brand-blue-500" />
            <span className="font-bold">{t('common.promoteBox')}</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 dark:hover:bg-ink-850 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        {/* Posting-as row */}
        <div className="px-6 py-3 bg-ink-50/60 dark:bg-ink-950/30 border-b border-ink-150 dark:border-ink-800">
          <div className="flex items-center gap-2 text-[12px] text-ink-500 dark:text-ink-400">
            <Avatar src={userInfo.profile_img} seed={userInfo.display_name} size={22} />
            <span className="font-semibold">{userInfo.display_name}</span>
            <span className="font-mono truncate">@{userInfo.user_id}@{userInfo.server}</span>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <ServerBadge host={userInfo.server} />
              <span>{t('mypage.promoteTo')}</span>
            </div>
          </div>
        </div>

        {/* Editable text */}
        <div className="px-6 pt-4 pb-1">
          <textarea
            rows={7}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-4 text-[14px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-blue-300 transition"
          />
          <div className="flex justify-end text-[12px] mt-1">
            <span className={text.length > charMax ? 'text-rose-500' : 'text-ink-400'}>
              {text.length}
            </span>
            <span className="text-ink-400"> / {charMax}</span>
          </div>
        </div>

        {/* Visibility + error */}
        <div className="px-6 pb-3 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{t('mypage.visibilityLabel')}</span>
            <div className="flex items-center gap-1 p-1 rounded-full bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800">
              {visibilityOptions.map((o) => {
                const on = visibility === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => setVisibility(o.v)}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12.5px] font-medium transition ${
                      on
                        ? 'bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100'
                        : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-100'
                    }`}
                  >
                    <o.i width={13} height={13} /> {o.l}
                  </button>
                );
              })}
            </div>
          </div>
          {error && <p className="text-[12px] text-rose-500">{error}</p>}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-ink-150 dark:border-ink-800 flex gap-3 justify-end">
          <Btn variant="ghost" size="md" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn
            variant="primary"
            size="md"
            onClick={handlePost}
            disabled={!text.trim() || text.length > charMax || loading}
          >
            <Icons.Globe width={14} height={14} />
            {loading ? t('common.posting') : t('mypage.promotePost')}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// ─── MyPage ───────────────────────────────────────────────────────────────────

const TABS: { v: ViewLevel; l: string }[] = [
  { v: 'answerd', l: 'Answered' },
  { v: 'not_answerd', l: 'Unanswered' },
  { v: 'all', l: 'All' },
];

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState<string>(searchParams.get('tab') ?? 'mybox');
  const [viewLevel, setViewLevel] = useState<ViewLevel>('not_answerd');
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [answerTarget, setAnswerTarget] = useState<Question | null>(null);
  const [reportId, setReportId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const { show: flash, ToastEl } = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    fetch('/api/v1/profile')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) { navigate('/login'); return; }
        setUserInfo(data);
      })
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!userInfo) return;
    fetch('/api/v1/count/questions')
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setUnansweredCount(d.count))
      .catch(() => {});
  }, [userInfo, refreshKey]);

  // ?q={id} で通知リンクから直接回答モーダルを開く
  useEffect(() => {
    const qId = searchParams.get('q');
    if (!qId || !userInfo) return;
    fetch(`/api/v1/question/single/${qId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((q: Question | null) => {
        if (q && q.answer === null) setAnswerTarget(q);
      })
      .catch(() => {});
  }, [userInfo, searchParams]);

  if (loading) return <LoadingScreen />;
  if (!userInfo) return null;

  const boxUrl = `/questionbox/@${userInfo.user_id}@${userInfo.server}`;
  const shareUrl = `${window.location.origin}${boxUrl}`;

  const writeToClipboard = async (text: string) => {
    if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(text); return; } catch {}
    }
    await navigator.clipboard.writeText(text);
  };

  const copyUrl = () => {
    writeToClipboard(shareUrl).then(() => flash(t('common.copied')));
  };

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      {ToastEl}
      <div className="flex md:h-screen max-w-[1440px] mx-auto">
        {/* Left Sidebar */}
        <aside className="hidden md:block w-64 shrink-0 border-r border-ink-150 dark:border-ink-800">
          <Sidebar
            activeKey={activeKey}
            onNavigate={(key) => {
              if (key === 'community') navigate('/community');
              else setActiveKey(key);
            }}
            unansweredCount={unansweredCount}
            userInfo={userInfo}
            onGoToBox={() => navigate(boxUrl)}
          />
        </aside>

        {/* Main */}
        <main className="flex-1 overflow-y-auto min-w-0 pb-20 md:pb-0">
          {activeKey === 'mybox' && (
            <>
              {/* Profile Header */}
              <div className="bg-white dark:bg-ink-900 border-b border-ink-150 dark:border-ink-800">
                {/* Banner */}
                <div
                  className="h-32 w-full"
                  style={{
                    background: 'repeating-linear-gradient(135deg, #bfd9f0 0, #bfd9f0 12px, #ddeeff 12px, #ddeeff 24px)',
                  }}
                />

                {/* Avatar + Actions row */}
                <div className="px-6 pt-0">
                  <div className="flex items-end justify-between -mt-9 mb-3">
                    <Avatar
                      src={userInfo.profile_img}
                      seed={userInfo.display_name}
                      size={72}
                      className="ring-4 ring-white dark:ring-ink-900 shadow-md"
                    />
                    <div className="flex items-center gap-1.5 sm:gap-2 pb-1 flex-wrap justify-end">
                      <button
                        onClick={() => setPromoteOpen(true)}
                        className="h-8 px-2.5 sm:px-3.5 rounded-full border border-brand-blue-300 dark:border-brand-blue-700 text-[12.5px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-brand-blue-50 dark:hover:bg-brand-blue-900/60 transition text-brand-blue-600 dark:text-brand-blue-300 shadow-sm"
                      >
                        <Icons.Globe width={12} height={12} />
                        <span className="hidden sm:inline">{t('common.promoteBox')}</span>
                      </button>
                      <button
                        onClick={copyUrl}
                        className="h-8 px-3.5 rounded-full border border-ink-200 dark:border-ink-700 text-[12.5px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 transition text-ink-700 dark:text-ink-200 shadow-sm"
                      >
                        <Icons.Link width={12} height={12} /> {t('common.copyUrl')}
                      </button>
                      <a
                        href={boxUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-8 px-3.5 rounded-full border border-ink-200 dark:border-ink-700 text-[12.5px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 transition text-ink-700 dark:text-ink-200 shadow-sm"
                      >
                        <Icons.ExternalLink width={12} height={12} /> {t('mypage.questionPage')}
                      </a>
                      <a
                        href={`https://${userInfo.server}/@${userInfo.user_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-8 px-3.5 rounded-full border border-ink-200 dark:border-ink-700 text-[12.5px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 transition text-ink-700 dark:text-ink-200 shadow-sm"
                      >
                        <Icons.ExternalLink width={12} height={12} /> {t('mypage.profile')}
                      </a>
                      <button
                        onClick={() => setActiveKey('settings')}
                        className="h-8 px-3.5 rounded-full border border-ink-200 dark:border-ink-700 text-[12.5px] font-medium flex items-center gap-1.5 bg-white dark:bg-ink-900 hover:bg-ink-100 dark:hover:bg-ink-800 transition text-ink-700 dark:text-ink-200 shadow-sm"
                      >
                        <Icons.Settings width={12} height={12} /> {t('common.edit')}
                      </button>
                    </div>
                  </div>

                  {/* Name + handle */}
                  <div className="mb-3">
                    <h1 className="text-[19px] font-extrabold leading-tight">
                      {userInfo.box_name || userInfo.display_name}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <a
                        href={`https://${userInfo.server}/@${userInfo.user_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[12px] text-ink-500 dark:text-ink-400 font-mono hover:underline"
                      >
                        @{userInfo.user_id}@{userInfo.server}
                      </a>
                      <ServerBadge host={userInfo.server} />
                    </div>
                  </div>

                  {/* Status badges */}
                  <div className="flex flex-wrap gap-2 pb-4">
                    <Tag tone={userInfo.accepting ? 'green' : 'outline'}>
                      {userInfo.accepting ? t('mypage.acceptingBadge') : t('mypage.notAcceptingBadge')}
                    </Tag>
                    {userInfo.notify && (
                      <Tag tone="blue">
                        <Icons.Bell width={11} height={11} /> {t('mypage.dmNotifyBadge')}
                      </Tag>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-t border-ink-150 dark:border-ink-800 px-2">
                  {TABS.map((tab) => (
                    <button
                      key={tab.v}
                      onClick={() => setViewLevel(tab.v)}
                      className={`px-5 py-3 text-[14px] font-medium border-b-2 transition -mb-px ${
                        viewLevel === tab.v
                          ? 'border-brand-blue-500 text-brand-blue-600 dark:text-brand-blue-300'
                          : 'border-transparent text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-100'
                      }`}
                    >
                      {tab.v === 'answerd' ? t('mypage.tabAnswered') : tab.v === 'not_answerd' ? t('mypage.tabNotAnswered') : t('mypage.tabAll')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Question list */}
              <div className="px-6 py-6">
                <QuestionInbox
                  viewLevel={viewLevel}
                  onSelectAnswer={setAnswerTarget}
                  onSelectReport={setReportId}
                  refreshKey={refreshKey}
                />
              </div>
            </>
          )}

          {activeKey === 'settings' && (
            <div className="px-6 py-6">
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setActiveKey('mybox')}
                  className="text-[13px] text-ink-500 hover:text-ink-800 dark:hover:text-ink-100 flex items-center gap-1 transition"
                >
                  <Icons.ChevronLeft width={16} height={16} /> {t('common.back')}
                </button>
                <h1 className="text-lg font-extrabold">{t('common.settings')}</h1>
              </div>
              <SettingsPanel userInfo={userInfo} />
            </div>
          )}
        </main>

        {/* Right Sidebar */}
        <aside className="hidden xl:block w-72 shrink-0 border-l border-ink-150 dark:border-ink-800 overflow-y-auto">
          <RightSidebar userInfo={userInfo} onAnswer={setAnswerTarget} refreshKey={refreshKey} />
        </aside>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden">
        <MobileNav
          activeKey={activeKey}
          onNavigate={(key) => {
            if (key === 'community') navigate('/community');
            else setActiveKey(key);
          }}
          unansweredCount={unansweredCount}
          onGoToBox={() => navigate(boxUrl)}
        />
      </div>

      {/* Modals */}
      {promoteOpen && userInfo && (
        <FediversePromoteModal
          userInfo={userInfo}
          boxUrl={boxUrl}
          onClose={() => setPromoteOpen(false)}
          onPosted={() => flash(t('community.promoteDone'))}
        />
      )}
      {answerTarget && userInfo && (
        <AnswerModal
          question={answerTarget}
          userInfo={userInfo}
          onClose={() => setAnswerTarget(null)}
          onAnswered={() => setRefreshKey((k) => k + 1)}
        />
      )}
      {reportId !== null && (
        <ReportModal
          questionId={reportId}
          onClose={() => setReportId(null)}
        />
      )}
    </div>
  );
};

export default MyPage;
